import React, { useMemo, useCallback, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
} from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import { useTheme, ThemeToggleButton } from '../../hooks/useTheme';
import 'chartjs-adapter-date-fns';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
);

const ChartVisualization = ({ data, onLoad, onError, isExpanded = false, forceDark = false }) => {
  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  
  // Theme management. The Gideon chat is always dark regardless of the app
  // theme, so it forces dark rendering via the forceDark prop.
  const { isDark: themeIsDark, getThemeColors: getThemeColorsBase, getChartColorPalette: getChartColorPaletteBase } = useTheme();
  const isDark = forceDark || themeIsDark;
  const getThemeColors = React.useCallback(() => getThemeColorsBase(isDark), [getThemeColorsBase, isDark]);
  const getChartColorPalette = React.useCallback(() => getChartColorPaletteBase(isDark), [getChartColorPaletteBase, isDark]);
  
  // Mobile and orientation detection with state
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [isLandscape, setIsLandscape] = React.useState(window.innerWidth > window.innerHeight);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [showControls, setShowControls] = React.useState(false);
  
  // Handle window resize and orientation changes
  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      const landscape = window.innerWidth > window.innerHeight;
      setIsMobile(mobile);
      setIsLandscape(landscape);
      
      // Auto-fullscreen on mobile landscape for better chart viewing
      if (mobile && landscape && !isExpanded) {
        setIsFullscreen(true);
      } else if (!landscape || !mobile) {
        setIsFullscreen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [isExpanded]);
  
  // State for showing/hiding volume on mobile
  const [showVolume, setShowVolume] = React.useState(!isMobile);

  // Dynamic container height based on mobile/landscape state
  const containerHeight = React.useMemo(() => {
    if (isExpanded) return '600px';
    if (isFullscreen && isLandscape && isMobile) return '80vh';
    if (isMobile && isLandscape) return '60vh';
    if (isMobile) return '240px'; // full column width now; ~3:2 for a daily series
    return '400px';
  }, [isExpanded, isFullscreen, isLandscape, isMobile, isDark, getThemeColors]);
  
  const fontSize = isExpanded ? 14 : (isMobile ? 10 : 12);

  // Extract data safely for hooks
  const { type, title, subtitle, data: chartData, options = {}, metadata } = data || {};
  const hasValidData = data && data.data && data.data.datasets && data.data.datasets.length > 0;

  // Enhanced chart data with theme-aware colors and styling
  const enhancedChartData = useMemo(() => {
    if (!hasValidData) return { datasets: [] };

    const themeColorPalette = getChartColorPalette();

    // Filter datasets on mobile - hide volume bars if showVolume is false
    let filteredDatasets = chartData.datasets || [];
    if (isMobile && !showVolume) {
      // Filter out volume/bar datasets on mobile by default
      filteredDatasets = filteredDatasets.filter(dataset => {
        const label = (dataset.label || '').toLowerCase();
        const isVolumeDataset = label.includes('volume') || dataset.type === 'bar';
        return !isVolumeDataset;
      });
    }

    return {
      ...chartData,
      datasets: filteredDatasets.map((dataset, index) => {
        let baseColor = dataset.borderColor || dataset.backgroundColor || themeColorPalette[index % themeColorPalette.length];
        // The backend hardcodes indigo-600, which is illegible on the dark
        // chat background — swap it for the light-indigo dark variant.
        if (isDark && typeof baseColor === 'string' && baseColor.toLowerCase() === '#4f46e5') {
          baseColor = '#818cf8';
        }

        // Generate background colors for different chart types
        let backgroundColor;
        if (type === 'pie_chart' || type === 'doughnut_chart') {
          backgroundColor = dataset.backgroundColor || themeColorPalette.slice(0, dataset.data?.length || themeColorPalette.length);
        } else {
          // For line/bar charts, use semi-transparent version
          backgroundColor = dataset.backgroundColor || (baseColor + (isDark ? '40' : '20')); // 25% opacity for dark, 12% for light
        }

        return {
          ...dataset,
          borderColor: baseColor,
          backgroundColor,
          borderWidth: dataset.borderWidth || (type === 'line_chart' ? 3 : 2),
          tension: type === 'line_chart' ? (dataset.tension || 0.4) : undefined,
          fill: type === 'line_chart' ? (dataset.fill !== undefined ? dataset.fill : false) : undefined,
          // ?? not ||: the backend sends pointRadius 0 on purpose — a dense
          // daily series reads as a line, not a string of beads.
          pointRadius: type === 'line_chart' ? (dataset.pointRadius ?? (isMobile ? 0 : 3)) : undefined,
          pointHoverRadius: type === 'line_chart' ? (dataset.pointHoverRadius ?? (isMobile ? 6 : 5)) : undefined,
          pointBackgroundColor: type === 'line_chart' ? (dataset.pointBackgroundColor || (isDark ? '#1e293b' : '#ffffff')) : undefined,
          pointBorderWidth: type === 'line_chart' ? (dataset.pointBorderWidth ?? 2) : undefined
        };
      })
    };
  }, [chartData, getChartColorPalette, isDark, type, hasValidData, isMobile, showVolume]);

  // Performance optimization: memoize chart options with mobile detection and theme support
  const memoizedOptions = useMemo(() => {
    const isVerySmall = window.innerWidth <= 480;
    const themeColors = getThemeColors();
    
    // Mobile-optimized spacing and sizing
    const mobilePadding = isMobile ? { left: 5, right: 15, top: 5, bottom: 5 } : { left: 10, right: 30, top: 10, bottom: 10 };
    const mobileFontSize = isVerySmall ? 9 : isMobile ? 10 : 11;
    const mobileTooltipSize = isMobile ? 12 : 13;
    
    // Default options for all charts with mobile optimization and theme support
    const defaultOptions = {
      responsive: true,
      maintainAspectRatio: false,
      devicePixelRatio: window.devicePixelRatio || 1,
      layout: {
        padding: mobilePadding
      },
      interaction: {
        intersect: false,
        mode: isMobile ? 'nearest' : 'index',
        // Enhanced touch support for mobile
        ...(isMobile && {
          axis: 'x'
        })
      },
      plugins: {
        legend: {
          position: isMobile ? 'bottom' : 'bottom',
          labels: {
            color: themeColors.text,
            padding: isMobile ? 10 : 20,
            usePointStyle: true,
            font: {
              family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              size: isMobile ? 10 : fontSize
            },
            boxWidth: isMobile ? 10 : 12,
            boxHeight: isMobile ? 10 : 12
          }
        },
        tooltip: {
          enabled: true,
          backgroundColor: themeColors.tooltip,
          titleColor: themeColors.text,
          bodyColor: themeColors.text,
          borderColor: themeColors.border,
          borderWidth: 1,
          cornerRadius: isMobile ? 6 : 8,
          padding: isMobile ? 8 : 12,
          titleFont: {
            size: isMobile ? 12 : 14,
            weight: 'bold'
          },
          bodyFont: {
            size: mobileTooltipSize
          },
          // Enhanced mobile touch interaction
          ...(isMobile && {
            displayColors: true,
            caretSize: 6,
            caretPadding: 8,
            external: null
          })
        }
      },
      scales: {
        x: {
          grid: {
            color: themeColors.grid,
            drawBorder: false
          },
          ticks: {
            font: {
              size: mobileFontSize
            },
            color: themeColors.text,
            maxRotation: isMobile ? 45 : 0,  // Slight rotation on mobile for readability
            autoSkip: true,
            autoSkipPadding: isMobile ? 15 : 10,
            maxTicksLimit: isMobile ? 4 : 8  // Fewer ticks on mobile to prevent overlap
          }
        },
        y: {
          position: 'left',
          grid: {
            color: themeColors.grid,
            drawBorder: false
          },
          ticks: {
            font: {
              size: mobileFontSize
            },
            color: themeColors.text,
            maxTicksLimit: isMobile ? 6 : 8,
            // Format numbers compactly
            callback: function(value) {
              if (value >= 1000000) {
                return (value / 1000000).toFixed(1) + 'M';
              } else if (value >= 1000) {
                return (value / 1000).toFixed(0) + 'K';
              } else if (value >= 100) {
                return value.toFixed(0);
              } else {
                return value.toFixed(2);
              }
            }
          }
        },
        // Secondary Y-axis for volume (right side)
        y1: {
          position: 'right',
          display: !isMobile || showVolume, // Hide on mobile if volume hidden
          grid: {
            drawOnChartArea: false // Don't draw grid lines for this axis
          },
          ticks: {
            font: {
              size: mobileFontSize
            },
            color: themeColors.text,
            maxTicksLimit: isMobile ? 4 : 6,
            // Format volume numbers compactly
            callback: function(value) {
              if (value >= 1000000000) {
                return (value / 1000000000).toFixed(1) + 'B';
              } else if (value >= 1000000) {
                return (value / 1000000).toFixed(0) + 'M';
              } else if (value >= 1000) {
                return (value / 1000).toFixed(0) + 'K';
              } else {
                return value.toFixed(0);
              }
            }
          }
        }
      },
      // Merge backend options EXCEPT scales and plugins: the backend's
      // scales replace the whole theme/mobile-aware block above (dead
      // Volume axis, indigo ticks on dark, uncapped $ labels).
      ...(() => {
        const { scales: _scales, plugins: _plugins, ...rest } = options;
        return rest;
      })()
    };
    return defaultOptions;
  }, [fontSize, options, isMobile, isLandscape, isFullscreen, showVolume]);

  // Notify when chart is loaded (only once)
  useEffect(() => {
    if (onLoad && hasValidData) {
      // Use setTimeout to avoid synchronous state updates
      const timer = setTimeout(() => {
        onLoad();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, []); // Empty dependency array to run only once

  // VALIDATION CHECKS AFTER ALL HOOKS
  if (!data) {
    if (onError) {
      onError('No chart data provided');
    }
    return (
      <div className="chart-no-data">
        <div className="no-data-icon">📊</div>
        <p>No chart data available</p>
        <small>Unable to generate visualization</small>
      </div>
    );
  }

  if (!hasValidData) {
    if (onError) {
      onError('Invalid chart data structure');
    }
    return (
      <div className="chart-error">
        <div className="error-icon">⚠️</div>
        <p>Chart data is incomplete</p>
        <small>Missing datasets or labels</small>
      </div>
    );
  }


  // Specific options for different chart types
  const getChartSpecificOptions = (chartType) => {
    switch (chartType) {
      case 'line_chart':
        return {
          ...memoizedOptions,
          elements: {
            point: {
              hoverRadius: 8,
              radius: 4
            }
          }
        };
      
      case 'bar_chart':
        return {
          ...memoizedOptions,
          plugins: {
            ...memoizedOptions.plugins,
            legend: {
              ...memoizedOptions.plugins.legend,
              display: enhancedChartData.datasets.length > 1
            }
          }
        };
      
      case 'pie_chart':
      case 'doughnut_chart':
        return {
          ...memoizedOptions,
          scales: undefined,
          plugins: {
            ...memoizedOptions.plugins,
            legend: {
              ...memoizedOptions.plugins.legend,
              position: 'right'
            }
          }
        };
      
      case 'candlestick_chart':
        // For candlestick, we'll use a line chart as fallback
        return {
          ...memoizedOptions,
          scales: {
            ...memoizedOptions.scales,
            y: {
              ...memoizedOptions.scales.y,
              beginAtZero: false
            }
          }
        };
      
      case 'sparkline':
        return {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              enabled: false
            }
          },
          scales: {
            x: {
              display: false
            },
            y: {
              display: false
            }
          },
          elements: {
            point: {
              radius: 0,
              hoverRadius: 3
            }
          }
        };
      
      default:
        return memoizedOptions;
    }
  };

  const finalOptions = getChartSpecificOptions(type);

  const renderChart = () => {
    const chartProps = {
      data: enhancedChartData,
      options: finalOptions
    };

    switch (type) {
      case 'line_chart':
      case 'sparkline':
        return <Line {...chartProps} />;
      
      case 'bar_chart':
        return <Bar {...chartProps} />;
      
      case 'pie_chart':
        return <Pie {...chartProps} />;
      
      case 'doughnut_chart':
        return <Doughnut {...chartProps} />;
      
      case 'candlestick_chart':
        // Fallback to line chart for candlestick (could be enhanced with custom plugin)
        return <Line {...chartProps} />;
      
      default:
        if (onError) {
          onError(`Unsupported chart type: ${type}`);
        }
        return (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            <p>Chart type "{type}" is not supported yet.</p>
            <p>Supported types: line_chart, bar_chart, pie_chart, sparkline</p>
          </div>
        );
    }
  };

  return (
    <>
      <div className={`modern-chart-container ${isMobile ? 'mobile' : ''} ${isLandscape ? 'landscape' : ''} ${isFullscreen ? 'fullscreen-active' : ''}`}>
        
        {/* Chart Header */}
        {(title || subtitle) && (
          <div className="chart-header">
            <div className="chart-header-content">
              {title && (
                <h3 className="chart-title">
                  <span className="title-icon">📈</span>
                  {title}
                </h3>
              )}
              {subtitle && <p className="chart-subtitle">{subtitle}</p>}
            </div>
            
            <div className="chart-header-actions">
              {isMobile && !isExpanded && (
                <>
                  <button
                    className="chart-action-btn"
                    onClick={() => setShowVolume(!showVolume)}
                    aria-label={showVolume ? 'Hide volume' : 'Show volume'}
                    style={{
                      color: showVolume ? '#818cf8' : 'var(--viz-text-secondary, #94a3b8)',
                      borderColor: showVolume ? 'rgba(129, 140, 248, 0.6)' : undefined
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M5 12h3v8H5zM10.5 4h3v16h-3zM16 9h3v11h-3z"/></svg>
                  </button>
                  <button
                    className="chart-action-btn"
                    onClick={() => setIsFullscreen(!isFullscreen)}
                    aria-label={isFullscreen ? 'Exit fullscreen' : 'View fullscreen'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M9 4H4v5M15 4h5v5M9 20H4v-5M15 20h5v-5"/></svg>
                  </button>
                </>
              )}
              {!isMobile && !forceDark && (
                <ThemeToggleButton size="small" className="desktop-theme-toggle" />
              )}
            </div>
          </div>
        )}
        
        {/* Chart Canvas */}
        <div className={`chart-canvas-wrapper ${isFullscreen ? 'fullscreen' : ''}`}>
          <div
            className={`chart-canvas ${type === 'sparkline' ? 'sparkline' : ''} ${isExpanded ? 'expanded' : ''} ${isMobile ? 'mobile' : ''} ${isLandscape && isMobile ? 'mobile-landscape' : ''}`}
            style={{
              height: type === 'sparkline' ? '60px' : containerHeight,
              position: 'relative',
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden',
              cursor: isMobile ? 'pointer' : 'default'
            }}
            onClick={isMobile && !isExpanded ? () => setIsFullscreen(true) : undefined}
          >
            {renderChart()}

            {/* Mobile tap hint - shows briefly */}
            {isMobile && !isExpanded && !isFullscreen && (
              <div
                className="mobile-tap-hint"
                style={{
                  position: 'absolute',
                  bottom: '12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: '#ffffff',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '11px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: 0.9,
                  pointerEvents: 'none',
                  animation: 'fadeOut 4s forwards',
                  zIndex: 10
                }}
              >
                <span>👆</span> Tap to expand
              </div>
            )}
          </div>
        </div>
      
      {/* Chart Footer with metadata */}
      {metadata && (
        <div className="chart-footer">
          <div className="metadata-info">
            {metadata.timeframe && (
              <span className="metadata-item">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2"/>
                </svg>
                {metadata.timeframe}
              </span>
            )}
            {metadata.source && (
              <span className="metadata-item">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z" stroke="currentColor" strokeWidth="2"/>
                  <line x1="16" y1="8" x2="2" y2="22" stroke="currentColor" strokeWidth="2"/>
                </svg>
                {metadata.source}
              </span>
            )}
          </div>
          {metadata.lastUpdated && (
            <div className="last-updated">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M1 4v6h6" stroke="currentColor" strokeWidth="2"/>
                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" stroke="currentColor" strokeWidth="2"/>
              </svg>
              {new Date(metadata.lastUpdated).toLocaleTimeString()}
            </div>
          )}
        </div>
      )}

        {/* Quality indicator */}
        <div className="chart-quality-indicator">
          <div className="quality-dot good" title="High quality data"></div>
          <span className="quality-text">Live Data</span>
        </div>
      </div>
      
      {/* Mobile Fullscreen Modal */}
      {isFullscreen && isMobile && (
        <div
          className="mobile-chart-fullscreen-modal"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: isDark ? '#0f172a' : '#ffffff',
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <div
            className="fullscreen-header"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              background: isDark
                ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
                : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              borderBottom: isDark
                ? '1px solid rgba(51, 65, 85, 0.6)'
                : '1px solid rgba(226, 232, 240, 0.6)',
              flexShrink: 0
            }}
          >
            <h2
              className="fullscreen-title"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: 0,
                fontSize: '18px',
                fontWeight: 700,
                color: isDark ? '#f1f5f9' : '#1e293b'
              }}
            >
              <span className="title-icon">📊</span>
              {title || 'Chart View'}
            </h2>
            
            {/* Enhanced Mobile Fullscreen Controls */}
            <div className="mobile-fullscreen-controls">
              {/* Volume Toggle in Fullscreen */}
              <button
                className="control-btn"
                onClick={() => setShowVolume(!showVolume)}
                title={showVolume ? "Hide volume" : "Show volume"}
                style={{
                  background: showVolume ? 'rgba(99, 102, 241, 0.3)' : 'var(--viz-bg-primary)',
                  color: showVolume ? '#a78bfa' : 'var(--viz-text-secondary)'
                }}
              >
                📊
              </button>

              <ThemeToggleButton size="small" className="fullscreen-theme-toggle" />

              <button
                className="close-fullscreen-btn"
                onClick={() => setIsFullscreen(false)}
                title="Exit fullscreen"
                style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#ef4444',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>
          </div>
          <div
            className="fullscreen-chart-content"
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              padding: '16px'
            }}
          >
            <div
              className="fullscreen-chart-canvas"
              style={{
                flex: 1,
                height: 'calc(100vh - 140px)',
                width: '100%',
                maxWidth: '100%',
                position: 'relative',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(51, 65, 85, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                borderRadius: '12px',
                border: isDark
                  ? '2px solid rgba(51, 65, 85, 0.6)'
                  : '2px solid rgba(226, 232, 240, 0.6)',
                overflow: 'hidden'
              }}
            >
              {renderChart()}
            </div>
            {metadata && (
              <div className="fullscreen-chart-footer">
                <div className="metadata-info">
                  {metadata.timeframe && (
                    <span className="metadata-item">
                      ⏱️ {metadata.timeframe}
                    </span>
                  )}
                  {metadata.source && (
                    <span className="metadata-item">
                      📊 {metadata.source}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ChartVisualization;